import { getProductListData, _getPRDataById } from '../products/mockData'
import { getTsdContactListData, _getTCTDataById } from '../tsdContacts/mockData'
import { getCompanyMSServiceDocumentListDataByCMSSId } from '../companyMSServiceDocuments/mockData'
import { getCompanyMSServiceNoteListDataByCMSSId } from '../companyMSServiceNotes/mockData'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import {
  getCompanyListData,
  getCompanyDataById,
  getCompanyOverviewData,
} from '../companies/mockData'
import {
  getCompanyMasterServiceDataById,
  getCompanyMasterServiceListData,
} from '../companyMasterServices/mockData'
import { _storeEntityDataListToLocalForage } from '../../modules/mockDataStorageManager'
import { getContractDataById } from '../contracts/mockData'
import { getSupplierProductListData } from '../supplierProducts/mockData'
import type { TEntityId } from '~/types/models'
import type {
  ICompanyMasterService,
  ICompanyMSService,
} from '~/types/company/models'
import type { TMasterService } from '~/types/masterService/models'
import type { IContract } from '~/types/contract/models'
import type { TProduct } from '~/types/product/models'
import { assertIsNotUndefined, assertIsString } from '~/types/models'
import {
  getCompanyMSServiceStatusById,
  CompanyMSServicesStatusIds,
} from '~/services/selectLists/companyMSServicesStatii'
import { getRecursPaidIntervalByLabel } from '~/services/selectLists'
import {
  getItemAvailabilityStateById,
  ItemAvailabilityIds,
} from '~/cynqueModules/dataObjects/ItemAvailabilityStatus'
import {
  MSService,
  buildMSServicesById,
} from '~/cynqueModules/dataObjects/MSService'
import { ContractStatusIds } from '~/services/selectLists/contractStatii'
import {
  isInstalledCompanyMSService,
  propagateCMSServicesData,
} from '~/cynqueModules/dataObjects/CompanyMSService'
import { getSupplierProductItemListData } from '../supplierProductItems/mockData'
import { setKeyIdentifierValues } from '../../modules/setKeyIdentifierValues'
import type { ISupplierProduct } from '~/types/supplierProduct/models'
import type { ISupplierProductItem } from '~/types/supplierProductItem/models'

const mn = '[api/msw/api/companyMSServices/mockData.ts]'

const companyMSServiceListData = undefined as unknown as ICompanyMSService[]

export const getCompanyMSServiceListData = getListDataFunctionFactory<
  ICompanyMSService[]
>({
  listData: companyMSServiceListData,
  storageKey: 'companymsservices',
  getStartingListData: getStartingCompanyMSServiceListData,
})

export async function getCompanyMSServiceDataById(
  id: string
): Promise<ICompanyMSService> {
  const list = await getCompanyMSServiceListData()
  if (!list) {
    throw new Error(`getCompanyMSServiceListData returned undefined/null`)
  }
  const cmss = list.find((cmss: ICompanyMSService) => cmss.id === id)
  if (!cmss) {
    throw new Error(`Can't find companyMSService for id '${id}'`)
  }
  await setKeyIdentifierValues([cmss])
  return cmss
}

export async function _getCMSSListDataByCMSId(
  id: TEntityId,
  list: ICompanyMSService[]
): Promise<ICompanyMSService[]> {
  if (!list) {
    throw new Error(`_getOMSSListDataByCMSId called with undefined/null list`)
  }
  const cmssList = list.filter(
    (cmss: ICompanyMSService) => cmss.masterService.id === id
  )
  // console.debug('11111111111111111111111111111111111111111111111111111111111')
  // console.debug(id, cmssList)
  // console.debug('11111111111111111111111111111111111111111111111111111111111')
  if (!cmssList) {
    throw new Error(
      `Can't find any companyMSServices for masterService id '${id}'`
    )
  }
  for (const cmss of cmssList) {
    cmss.companyMSServiceDocuments =
      await getCompanyMSServiceDocumentListDataByCMSSId(cmss.id)
    cmss.companyMSServiceNotes = await getCompanyMSServiceNoteListDataByCMSSId(
      cmss.id
    )
  }
  await setKeyIdentifierValues(cmssList)
  return cmssList
}

export async function getCompanyMSServiceListDataByCMSId(
  id: string
): Promise<ICompanyMSService[]> {
  const list = await getCompanyMSServiceListData()
  return _getCMSSListDataByCMSId(id, list)
}

/**
 * The company msServices at a location -- with `doIncludeWholeKits`, as the
 * Location overview wants them: every row of every kit that has ANY row there,
 * since a kit item can be at a different location from its kit
 * (WXTG/cynque-api-server#1773). A kit is found by walking each row up to its
 * root; a Set's header is never stored, so its members already point past it.
 */
export async function getCMSSListDataByLocationId(
  id: TEntityId,
  installedCompanyMSServicesOnly = false,
  doIncludeWholeKits = false
): Promise<ICompanyMSService[]> {
  const list = await getCompanyMSServiceListData()
  if (!list) {
    throw new Error(`getCompanyMSServiceListData returned undefined`)
  }
  const byId = new Map(list.map((cmss) => [cmss.id, cmss]))
  const getRootId = (cmss: ICompanyMSService) => {
    let root = cmss
    while (root.parentMSServiceId && byId.has(root.parentMSServiceId)) {
      root = byId.get(root.parentMSServiceId) as ICompanyMSService
    }
    return root.id
  }
  const rootIdsHere = new Set(
    list
      .filter((cmss: ICompanyMSService) => cmss.location?.id === id)
      .map(getRootId)
  )
  const cmssList: ICompanyMSService[] = list.filter(
    (cmss: ICompanyMSService) =>
      (doIncludeWholeKits
        ? rootIdsHere.has(getRootId(cmss))
        : cmss.location?.id === id) &&
      (!installedCompanyMSServicesOnly ||
        (installedCompanyMSServicesOnly && isInstalledCompanyMSService(cmss)))
  )
  if (!cmssList) {
    console.debug(`Can't find any companyMSServices for location id '${id}'`)
  }
  for (const cmss of cmssList) {
    if (cmss.contract) {
      try {
        const contract = await getContractDataById(cmss.contract.id)
        assertIsNotUndefined(contract) // Should never be
        cmss.contract.hrid = contract.hrid
        cmss.contract.contractNumber = contract.contractNumber
        cmss.contract.endDate = contract.endDate
      } catch (e) {
        throw new Error(`getContractDataById (${cmss.contract.id}) failed`)
      }
    }
  }
  // console.debug('11111111111111111111111111111111111111111111111111111111111')
  // console.debug(
  //   `installedCompanyMSServicesOnly`,
  //   installedCompanyMSServicesOnly
  // )
  // console.debug(id, cmssList)
  // console.debug('11111111111111111111111111111111111111111111111111111111111')

  await setKeyIdentifierValues(cmssList)
  return cmssList ?? []
}

export async function getCMSSListDataByContractId(
  id: TEntityId
): Promise<ICompanyMSService[]> {
  const list = await getCompanyMSServiceListData()
  if (!list) {
    throw new Error(`getCompanyMSServiceListData returned undefined`)
  }
  const cmssList = list.filter(
    (cmss: ICompanyMSService) => cmss.contract?.id === id
  )
  // console.debug('11111111111111111111111111111111111111111111111111111111111')
  // console.debug(id, cmssList)
  // console.debug('11111111111111111111111111111111111111111111111111111111111')
  if (!cmssList) {
    throw new Error(`Can't find any companyMSServices for contract id '${id}'`)
  }
  await setKeyIdentifierValues(cmssList)
  return cmssList
}

export async function updateCMSSsOnContractIfContractWasAccepted({
  targetItem,
  updatedEntityData,
}: {
  targetItem: IContract
  updatedEntityData: Partial<IContract>
}) {
  const fn = `${mn} updateCMSSsOnContractIfContractWasAccepted |`
  // console.debug('11111111111111111111111111111111111111111111111111111111111')
  // console.debug(targetItem, updatedEntityData)
  // console.debug('11111111111111111111111111111111111111111111111111111111111')
  if (!('status' in updatedEntityData)) {
    console.debug(
      `${fn} 'status' was not in updatedEntityData. Not changing status in CMSSs`
    )
    return
  }
  console.debug(`${fn} 'status' -WAS- in updatedEntityData. Updating CMSSs.`)
  const cmssList = await getCMSSListDataByContractId(targetItem.id)
  assertIsNotUndefined(cmssList)
  if (targetItem.status.id === ContractStatusIds.ACCEPTED) {
    let somethingChanged = false
    cmssList.forEach((cmss) => {
      if (
        cmss.status.id === CompanyMSServicesStatusIds.PREORDER_UNLOCKED ||
        cmss.status.id === CompanyMSServicesStatusIds.PREORDER_LOCKED
      ) {
        console.debug(
          `${fn} Updating ${cmss.status.label} to READY_FOR_SERVICEORDER.`
        )
        somethingChanged = true
        cmss.status = getCompanyMSServiceStatusById(
          CompanyMSServicesStatusIds.READY_FOR_SERVICEORDER
        )
      }
    })
    if (!somethingChanged) {
      // Should always be true. (Belt and suspenders.)
      return
    }
    await _storeEntityDataListToLocalForage({
      entityKeyBase: 'companymsservices',
      listData: cmssList,
      callingFn: fn,
    })
  }
}

export async function getCompanyDataByCompanyMSServiceId(id: string) {
  const cmss = await getCompanyMSServiceDataById(id)
  if (!cmss) {
    throw new Error(`getCompanyMSServiceDataById returned undefined/null`)
  }
  assertIsString(cmss.masterService.id)
  const cms = await getCompanyMasterServiceDataById(cmss.masterService.id)
  if (!cms) {
    throw new Error(`getCompanyMasterSServiceDataById returned undefined/null`)
  }
  assertIsString(cms.company.id)
  const company = await getCompanyDataById(cms.company.id)
  if (!cms) {
    throw new Error(`getCompanyMasterSServiceDataById returned undefined/null`)
  }
  return company
}

export async function updateMasterServiceInCompanyMSServices({
  targetItem: masterService,
}: {
  targetItem: TMasterService
}) {
  const fn = `${mn} updateMasterServiceInCompanyMSServices |`
  const list: ICompanyMSService[] = await getCompanyMSServiceListData()
  if (!list) {
    throw new Error(`getCompanyMSServiceListData returned undefined/null`)
  }
  for (const companyMSService of list) {
    if (companyMSService.masterService.id === masterService.id) {
      console.debug(
        `${fn} updating masterService.label in companyMSService ` +
          ` from ${companyMSService.masterService.label} to ${masterService.descr}`
      )
      companyMSService.masterService = {
        id: masterService.id,
        label: masterService.descr,
        parent: companyMSService.masterService.parent,
      }
    }
  }
  await _storeEntityDataListToLocalForage({
    entityKeyBase: 'companymsservices',
    listData: list,
    callingFn: fn,
  })
}

//
// Propagation Methods
//

export async function propagateCompanyMSServiceOrServicesAdditions({
  newEntityData,
}: {
  newEntityData: ICompanyMSService | ICompanyMSService[]
}) {
  const fn = `${mn} propagateCompanyMSServiceOrServicesAdditions |`
  if (!newEntityData) {
    console.debug(`${fn} newEntityData is falsey. Nothing to propagate.`)
    return
  }
  if (Array.isArray(newEntityData)) {
    if (newEntityData.length < 1) {
      console.debug(
        `${fn} newEntityData is an empty array. Nothing to propagate.`
      )
      return
    }
    _propagateCompanyMSServiceAdditionsChangesOrDeletions({
      companyMSService: newEntityData[0],
    })
    return
  }
  _propagateCompanyMSServiceAdditionsChangesOrDeletions({
    companyMSService: newEntityData,
  })
}

export async function propagateCompanyMSServicesChanges({
  updatedEntityDataMultiple,
}: {
  updatedEntityDataMultiple: Partial<ICompanyMSService>[]
}) {
  const fn = `${mn} propagateCompanyMSServicesChanges |`
  if (
    !updatedEntityDataMultiple ||
    !Array.isArray(updatedEntityDataMultiple) ||
    updatedEntityDataMultiple.length < 1
  ) {
    console.debug(
      `${fn} updatedEntityDataMultiple is falsey ` +
        ` or an empty array. Nothing to propagate.`
    )
    return
  }
  const updatedEntityData = updatedEntityDataMultiple[0]
  if (!updatedEntityData) {
    throw new Error(
      `${fn} updatedEntityData is falsey after testing truthy.` +
        ` This should never happen.`
    )
  }
  assertIsString(updatedEntityData.id) // Should always be
  const cmss = await getCompanyMSServiceDataById(updatedEntityData.id)
  if (!cmss) {
    throw new Error(`getCompanyMSServiceDataById returned undefined/null`)
  }
  _propagateCompanyMSServiceAdditionsChangesOrDeletions({
    companyMSService: cmss,
  })
}

export async function propagateCompanyMSServiceChanges({
  targetItem,
}: {
  targetItem: ICompanyMSService
}) {
  const fn = `${mn} propagateCompanyMSServiceChanges |`
  if (!targetItem) {
    console.debug(`${fn} targetItem is falsey. Nothing to propagate.`)
    return
  }
  _propagateCompanyMSServiceAdditionsChangesOrDeletions({
    companyMSService: targetItem,
  })
}

export async function propagateCompanyMSServiceDeletions({
  deletedEntityData,
}: {
  deletedEntityData: ICompanyMSService
}) {
  const fn = `${mn} propagateCompanyMSServiceDeletions |`
  if (!deletedEntityData) {
    console.debug(`${fn} deletedEntityData is falsey. Nothing to propagate.`)
    return
  }
  _propagateCompanyMSServiceAdditionsChangesOrDeletions({
    companyMSService: deletedEntityData,
  })
}

async function _propagateCompanyMSServiceAdditionsChangesOrDeletions({
  companyMSService,
}: {
  companyMSService: ICompanyMSService
}) {
  function _setUpCompanyMSServices() {
    // Indexed for the same reason the app pages are: this runs on every mock
    // write, and the parent-kit lookup inside MSService is otherwise a full
    // scan per msService.
    const msServicesById = buildMSServicesById(allCMSServices)
    allCMSServices.forEach((cmsService) => {
      cmsService = Object.assign(
        cmsService,
        MSService(cmsService, allCMSServices, msServicesById)
        // Note: There are some properties being set by MSService
        // that really don't need to be set server-side, cause
        // they're for display only, but don't want to wrangle
        // two versions of the MSService dataObject so leaving
        // this be for the moment.
      )
    })
  }
  //
  // Execution begins here
  //
  const fn = `${mn} _propagateCompanyMSServiceAdditionsChangesOrDeletions |`
  if (!companyMSService) {
    console.debug(`${fn} companyMSService is falsey. Nothing to propagate.`)
    return
  }
  const cms = await getCompanyMasterServiceDataById(
    companyMSService.masterService.id
  )
  if (!cms) {
    throw new Error(`getCompanyMasterServiceDataById returned undefined/null`)
  }
  const company = await getCompanyDataById(cms.company.id)
  if (!company) {
    throw new Error(`getCompanyDataById returned undefined/null`)
  }
  const overviewData = await getCompanyOverviewData(company.id)
  assertIsNotUndefined(overviewData)
  console.debug(`${fn} overviewData:`, overviewData)

  const allCMSServices: ICompanyMSService[] = overviewData.companyMasterServices
    .filter((cms: ICompanyMasterService) => !!cms.companyMSServices)
    .map((cms: TMasterService) => cms.companyMSServices)
    .flat()
  console.debug(`${fn} all mcmss:`, allCMSServices)

  propagateCMSServicesData({
    overviewData,
    allCMSServices,
    setUpCompanyMSServices: _setUpCompanyMSServices,
  })
  console.debug(`${fn} company after propagation:`, overviewData.company)

  const companyList = await getCompanyListData()
  if (!companyList) {
    throw new Error(`getCompanyListData returned undefined/null`)
  }
  await _storeEntityDataListToLocalForage({
    entityKeyBase: 'company',
    listData: companyList,
    callingFn: fn,
  })

  const companyMasterServiceList = await getCompanyMasterServiceListData()
  if (!companyMasterServiceList) {
    throw new Error(`getCompanyMasterServiceListData returned undefined/null`)
  }
  console.debug(`${fn} companyMasterServiceList:`, companyMasterServiceList)
  await _storeEntityDataListToLocalForage({
    entityKeyBase: 'companymasterservices',
    listData: companyMasterServiceList,
    callingFn: fn,
  })
}

/**
 * An INSTALLED kit in the company silo: Metropolis Bank's "Internet" group
 * (XXCMS0004), a copy of the Waltham Cogent DIA kit tree from the DIA group --
 * the kit, its Fiber Converter (an item also sold on its own) and its three
 * kit-only items (Port, Access, IP Block) -- made ACTIVE, and moved to BOSTON.
 *
 * ⚠ It exists because nothing else could show a filter over INSTALLED company
 * services excluding anything: the DIA group is all Ready for Contract, which the
 * Case and Service Request Service selects hide anyway, and the only installed
 * rows were ten standalone POTS lines. Copied rather than flipping DIA to
 * ACTIVE, because three specs need DIA Ready for Contract:
 * msServiceQuantityValidation (Edit is live on CMSS0021 because of it),
 * overviewInPlaceReactivity (asserts the group's 'Ready for Contracts') and
 * keyIdentifierFromFieldSet (the DIA rows that carry fieldSetData).
 *
 * ⚠ BOSTON, not Waltham where the source rows sit. A Locations overview lists
 * every INSTALLED msService at that location, and keyIdentifierFromFieldSet
 * counts the distinct Key Identifiers on Waltham's: an active kit there added
 * two values to its five phone numbers (measured 7). Boston's location page is
 * read by no spec, and its name matches no search spec's term.
 */
function _installedInternetKitRows(
  rows: ICompanyMSService[]
): ICompanyMSService[] {
  const sourceIds = [
    'XXCMSS0021', // Cogent DIA (Kit)
    'XXCMSS0028',
    'XXCMSS0034',
    'XXCMSS0038',
    'XXCMSS0044',
  ]
  const copyId = (sourceId: string) =>
    `XXCMSS0${106 + sourceIds.indexOf(sourceId)}`
  return sourceIds.map((sourceId) => {
    const source = rows.find((row) => row.id === sourceId)
    if (!source) {
      throw new Error(`${mn} _installedInternetKitRows: no ${sourceId}`)
    }
    const id = copyId(sourceId)
    return {
      ...source,
      id,
      hrid: id.slice(2),
      masterService: {
        id: 'XXCMS0004',
        label: 'Internet',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      parentMSServiceId: source.parentMSServiceId
        ? copyId(source.parentMSServiceId)
        : undefined,
      location: { id: 'XXL0001', label: 'Boston' },
      status: getCompanyMSServiceStatusById(CompanyMSServicesStatusIds.ACTIVE)!,
      version: '1',
    }
  })
}

//
//
//

export async function getStartingCompanyMSServiceListData(): Promise<
  ICompanyMSService[]
> {
  const prListData = await getProductListData()
  const tctListData = await getTsdContactListData()

  const pr0006 = await _getPRDataById('XXPR0006', prListData, true)
  const pr0007 = await _getPRDataById('XXPR0007', prListData, true)
  const pr0035 = await _getPRDataById('XXPR0035', prListData, true)

  if (!pr0007 || !pr0035) {
    throw new Error('Some mock data products are falsey')
  }

  function isDefined<T>(
    value: NonNullable<T> | undefined | null
  ): value is NonNullable<T> {
    return value !== null && value !== undefined
  }

  function isEachItemDefined<T>(
    value: Array<NonNullable<T> | undefined | null>
  ): value is Array<NonNullable<T>> {
    return value.every(isDefined)
  }

  const prArray = []
  for (let i = 35; i < 64; i++) {
    if (i === 41) {
      continue
    }
    const id = `${'XXPR00'}${('00' + i.toString()).slice(-2)}`
    prArray[i] = await _getPRDataById(id, prListData, true)
  }

  if (!isEachItemDefined<TProduct>(prArray)) {
    throw new Error('Some mock data products are falsey')
  }

  const spListData: ISupplierProduct[] = await getSupplierProductListData()
  const spiListData: ISupplierProductItem[] =
    await getSupplierProductItemListData()

  function findSPI(
    productId: string,
    supplierId: string
  ): ISupplierProductItem | undefined {
    return spiListData.find(
      (spi) =>
        spi.product.id === productId &&
        spi.supplierProduct.supplier.id === supplierId
    )
  }

  function findSP(
    productId: string,
    supplierId: string
  ): ISupplierProduct | undefined {
    return spListData.find(
      (sp) => sp.product.id === productId && sp.supplier.id === supplierId
    )
  }

  //
  //  START MOCK DATA
  //
  //

  const startingRows: ICompanyMSService[] = [
    {
      id: 'XXCMSS0021',
      hrid: 'CMSS0021',
      descr: findSP(prArray[35].id, 'XXS0002')?.descr || prArray[35].descr,
      masterService: {
        id: 'XXCMS0001',
        label: 'DIA',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: prArray[35],
      parentProduct: undefined,
      parentMSServiceId: undefined,
      location: {
        id: 'XXL0009',
        label: 'Waltham',
      },
      supplier: {
        id: 'XXS0002',
        label: 'Cogent',
      },
      supplierProduct: (() => {
        const _sp = findSP(prArray[35].id, 'XXS0002')
        return _sp ? { id: _sp.id, label: _sp.descr } : undefined
      })(),
      tsd: {
        id: 'XXT0001',
        hrid: 'T0001',
        descr: 'TBI',
        splitFraction: 0.85,
        spendAmount: 165000,
        numOpenOpportunities: 4,
        numSuppliers: 3,
        numCompanys: 180,
        channelManager: _getTCTDataById('XXTCT0002', tctListData),
        relationship: {
          id: 1,
          label: 'Direct',
        },
        lastActivityDate: '2022-03-01',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: false,
      },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: '--',
      splitFraction: null,
      kitQuantity: '--',
      quantity: 1,
      unitRcAmount: 0,
      mrcAmount: 0,
      unitNrcAmount: 0,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      closeByDate: '2022-04-01',
      status: getCompanyMSServiceStatusById(
        CompanyMSServicesStatusIds.READY_FOR_CONTRACT
      )!,
      doInclude: true,
      fieldSetData:
        '{ "circuitType": "T1", "circuitId": "987654", "handoff": "Multi Mode - 1 Gbps",' +
        ' "autoNegotiation": "Off", "connector": "Optical", "vlanSetTo": "Untagged",' +
        ' "managed": true, "circuitId": "E92-1029333", "gatewayIp": "222.143.100.1",' +
        ' "subnetMask": "255.255.255.0", "broadcastIp": "192.168.10.255",' +
        ' "startingIp": "222.143.100.50",' +
        ' "lecDemark": "543 Oingo Boingo Blvd, Suite 200, San Diego, CA 90234",' +
        ' "carrierEquipment": "Cisco", "equipmentDetails": "Rack 5, 2nd floor" }',
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCMSS0028',
      hrid: 'CMSS0028',
      descr: findSPI(prArray[44].id, 'XXS0002')?.descr || prArray[44].descr,
      masterService: {
        id: 'XXCMS0001',
        label: 'DIA',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: prArray[44],
      parentProduct: {
        id: 'XXPR0035',
        label: 'DIA - Kit',
      },
      parentMSServiceId: 'XXCMSS0021',
      location: {
        id: 'XXL0009',
        label: 'Waltham',
      },
      supplier: {
        id: 'XXS0002',
        label: 'Cogent',
      },
      supplierProductItem: findSPI(prArray[44].id, 'XXS0002'),
      itemAvailabilityState: getItemAvailabilityStateById(
        ItemAvailabilityIds.OPTIONAL_INCLUDED
      ),
      tsd: {
        id: 'XXT0001',
        hrid: 'T0001',
        descr: 'TBI',
        splitFraction: 0.85,
        spendAmount: 165000,
        numOpenOpportunities: 4,
        numSuppliers: 3,
        numCompanys: 180,
        channelManager: _getTCTDataById('XXTCT0002', tctListData),
        relationship: {
          id: 1,
          label: 'Direct',
        },
        lastActivityDate: '2022-03-01',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: false,
      },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.18,
      term: 36,
      kitQuantity: 'kit',
      quantity: 1,
      unitRcAmount: 60,
      mrcAmount: 0,
      unitNrcAmount: 500,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: '2022-10-01',
      closeByDate: '2022-04-01',
      status: getCompanyMSServiceStatusById(
        CompanyMSServicesStatusIds.READY_FOR_CONTRACT
      )!,
      doInclude: true,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCMSS0034',
      hrid: 'CMSS0034',
      descr: findSPI(prArray[51].id, 'XXS0002')?.descr || prArray[51].descr,
      masterService: {
        id: 'XXCMS0001',
        label: 'DIA',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: prArray[51],
      parentProduct: {
        id: 'XXPR0035',
        label: 'DIA - Kit',
      },
      parentMSServiceId: 'XXCMSS0021',
      location: {
        id: 'XXL0009',
        label: 'Waltham',
      },
      supplier: {
        id: 'XXS0002',
        label: 'Cogent',
      },
      supplierProductItem: findSPI(prArray[51].id, 'XXS0002'),
      itemAvailabilityState: getItemAvailabilityStateById(
        ItemAvailabilityIds.OPTIONAL_INCLUDED
      ),
      tsd: {
        id: 'XXT0001',
        hrid: 'T0001',
        descr: 'TBI',
        splitFraction: 0.85,
        spendAmount: 165000,
        numOpenOpportunities: 4,
        numSuppliers: 3,
        numCompanys: 180,
        channelManager: _getTCTDataById('XXTCT0002', tctListData),
        relationship: {
          id: 1,
          label: 'Direct',
        },
        lastActivityDate: '2022-03-01',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: false,
      },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.18,
      term: 36,
      kitQuantity: 'kit',
      quantity: 1,
      unitRcAmount: 100,
      mrcAmount: 0,
      unitNrcAmount: 1000,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: '2022-10-01',
      closeByDate: '2022-04-01',
      status: getCompanyMSServiceStatusById(
        CompanyMSServicesStatusIds.READY_FOR_CONTRACT
      )!,
      doInclude: true,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCMSS0038',
      hrid: 'CMSS0038',
      descr: findSPI(prArray[56].id, 'XXS0002')?.descr || prArray[56].descr,
      masterService: {
        id: 'XXCMS0001',
        label: 'DIA',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: prArray[56],
      parentProduct: {
        id: 'XXPR0035',
        label: 'DIA - Kit',
      },
      parentMSServiceId: 'XXCMSS0021',
      location: {
        id: 'XXL0009',
        label: 'Waltham',
      },
      supplier: {
        id: 'XXS0002',
        label: 'Cogent',
      },
      supplierProductItem: findSPI(prArray[56].id, 'XXS0002'),
      itemAvailabilityState: getItemAvailabilityStateById(
        ItemAvailabilityIds.OPTIONAL_INCLUDED
      ),
      tsd: {
        id: 'XXT0001',
        hrid: 'T0001',
        descr: 'TBI',
        splitFraction: 0.85,
        spendAmount: 165000,
        numOpenOpportunities: 4,
        numSuppliers: 3,
        numCompanys: 180,
        channelManager: _getTCTDataById('XXTCT0002', tctListData),
        relationship: {
          id: 1,
          label: 'Direct',
        },
        lastActivityDate: '2022-03-01',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: false,
      },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.18,
      term: 36,
      kitQuantity: 'kit',
      quantity: 1,
      unitRcAmount: 79,
      mrcAmount: 0,
      unitNrcAmount: 150,
      nrcAmount: 0,
      oneTimeAmount: 250,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: '2022-10-01',
      closeByDate: '2022-04-01',
      status: getCompanyMSServiceStatusById(
        CompanyMSServicesStatusIds.READY_FOR_CONTRACT
      )!,
      doInclude: true,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCMSS0044',
      hrid: 'CMSS0044',
      descr: findSPI(prArray[62].id, 'XXS0002')?.descr || prArray[62].descr,
      masterService: {
        id: 'XXCMS0001',
        label: 'DIA',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: prArray[62],
      parentProduct: {
        id: 'XXPR0035',
        label: 'DIA - Kit',
      },
      parentMSServiceId: 'XXCMSS0021',
      location: {
        id: 'XXL0009',
        label: 'Waltham',
      },
      supplier: {
        id: 'XXS0002',
        label: 'Cogent',
      },
      supplierProductItem: findSPI(prArray[62].id, 'XXS0002'),
      itemAvailabilityState: getItemAvailabilityStateById(
        ItemAvailabilityIds.OPTIONAL_INCLUDED
      ),
      tsd: {
        id: 'XXT0001',
        hrid: 'T0001',
        descr: 'TBI',
        splitFraction: 0.85,
        spendAmount: 165000,
        numOpenOpportunities: 4,
        numSuppliers: 3,
        numCompanys: 180,
        channelManager: _getTCTDataById('XXTCT0002', tctListData),
        relationship: {
          id: 1,
          label: 'Direct',
        },
        lastActivityDate: '2022-03-01',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: false,
      },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.18,
      term: 36,
      kitQuantity: 2,
      quantity: 1,
      unitRcAmount: 0,
      mrcAmount: 0,
      unitNrcAmount: 250,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: true,
      quoteDate: '2022-10-01',
      closeByDate: '2022-04-01',
      status: getCompanyMSServiceStatusById(
        CompanyMSServicesStatusIds.READY_FOR_CONTRACT
      )!,
      doInclude: true,
      version: '1',
      canBeDeleted: true,
    },
    //
    // Cogent / Lake
    //
    {
      id: 'XXCMSS0071',
      hrid: 'CMSS0071',
      descr: findSP(prArray[35].id, 'XXS0002')?.descr || prArray[35].descr,
      masterService: {
        id: 'XXCMS0001',
        label: 'DIA',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: prArray[35],
      parentProduct: undefined,
      parentMSServiceId: undefined,
      location: {
        id: 'XXL0010',
        label: 'Sunset',
      },
      supplier: {
        id: 'XXS0002',
        label: 'Cogent',
      },
      supplierProduct: (() => {
        const _sp = findSP(prArray[35].id, 'XXS0002')
        return _sp ? { id: _sp.id, label: _sp.descr } : undefined
      })(),
      tsd: {
        id: 'XXT0001',
        hrid: 'T0001',
        descr: 'TBI',
        splitFraction: 0.85,
        spendAmount: 165000,
        numOpenOpportunities: 4,
        numSuppliers: 3,
        numCompanys: 180,
        channelManager: _getTCTDataById('XXTCT0002', tctListData),
        relationship: {
          id: 1,
          label: 'Direct',
        },
        lastActivityDate: '2022-03-01',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: false,
      },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: '--',
      splitFraction: null,
      kitQuantity: '--',
      quantity: 1,
      unitRcAmount: 0,
      mrcAmount: 0,
      unitNrcAmount: 0,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      closeByDate: '2022-04-01',
      status: getCompanyMSServiceStatusById(
        CompanyMSServicesStatusIds.READY_FOR_CONTRACT
      )!,
      doInclude: true,
      fieldSetData:
        '{ "circuitType": "T1", "circuitId": "987654", "handoff": "Multi Mode - 1 Gbps",' +
        ' "autoNegotiation": "Off", "connector": "Optical", "vlanSetTo": "Untagged",' +
        ' "managed": true, "circuitId": "E92-1029333", "gatewayIp": "222.143.100.1",' +
        ' "subnetMask": "255.255.255.0", "broadcastIp": "192.168.10.255",' +
        ' "startingIp": "222.143.100.50",' +
        ' "lecDemark": "543 Oingo Boingo Blvd, Suite 200, San Diego, CA 90234",' +
        ' "carrierEquipment": "Cisco", "equipmentDetails": "Rack 5, 2nd floor" }',
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCMSS0078',
      hrid: 'CMSS0078',
      descr: findSPI(prArray[44].id, 'XXS0002')?.descr || prArray[44].descr,
      masterService: {
        id: 'XXCMS0001',
        label: 'DIA',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: prArray[44],
      parentProduct: {
        id: 'XXPR0035',
        label: 'DIA - Kit',
      },
      parentMSServiceId: 'XXCMSS0071',
      location: {
        id: 'XXL0010',
        label: 'Sunset',
      },
      supplier: {
        id: 'XXS0002',
        label: 'Cogent',
      },
      supplierProductItem: findSPI(prArray[44].id, 'XXS0002'),
      itemAvailabilityState: getItemAvailabilityStateById(
        ItemAvailabilityIds.OPTIONAL_INCLUDED
      ),
      tsd: {
        id: 'XXT0001',
        hrid: 'T0001',
        descr: 'TBI',
        splitFraction: 0.85,
        spendAmount: 165000,
        numOpenOpportunities: 4,
        numSuppliers: 3,
        numCompanys: 180,
        channelManager: _getTCTDataById('XXTCT0002', tctListData),
        relationship: {
          id: 1,
          label: 'Direct',
        },
        lastActivityDate: '2022-03-01',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: false,
      },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.18,
      term: 36,
      kitQuantity: 'kit',
      quantity: 1,
      unitRcAmount: 60,
      mrcAmount: 0,
      unitNrcAmount: 500,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: '2022-10-01',
      closeByDate: '2022-04-01',
      status: getCompanyMSServiceStatusById(
        CompanyMSServicesStatusIds.READY_FOR_CONTRACT
      )!,
      doInclude: true,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCMSS0084',
      hrid: 'CMSS0084',
      descr: findSPI(prArray[51].id, 'XXS0002')?.descr || prArray[51].descr,
      masterService: {
        id: 'XXCMS0001',
        label: 'DIA',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: prArray[51],
      parentProduct: {
        id: 'XXPR0035',
        label: 'DIA - Kit',
      },
      parentMSServiceId: 'XXCMSS0071',
      location: {
        id: 'XXL0010',
        label: 'Sunset',
      },
      supplier: {
        id: 'XXS0002',
        label: 'Cogent',
      },
      supplierProductItem: findSPI(prArray[51].id, 'XXS0002'),
      itemAvailabilityState: getItemAvailabilityStateById(
        ItemAvailabilityIds.OPTIONAL_INCLUDED
      ),
      tsd: {
        id: 'XXT0001',
        hrid: 'T0001',
        descr: 'TBI',
        splitFraction: 0.85,
        spendAmount: 165000,
        numOpenOpportunities: 4,
        numSuppliers: 3,
        numCompanys: 180,
        channelManager: _getTCTDataById('XXTCT0002', tctListData),
        relationship: {
          id: 1,
          label: 'Direct',
        },
        lastActivityDate: '2022-03-01',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: false,
      },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.18,
      term: 36,
      kitQuantity: 'kit',
      quantity: 1,
      unitRcAmount: 100,
      mrcAmount: 0,
      unitNrcAmount: 1000,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: '2022-10-01',
      closeByDate: '2022-04-01',
      status: getCompanyMSServiceStatusById(
        CompanyMSServicesStatusIds.READY_FOR_CONTRACT
      )!,
      doInclude: true,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCMSS0088',
      hrid: 'CMSS0088',
      descr: findSPI(prArray[56].id, 'XXS0002')?.descr || prArray[56].descr,
      masterService: {
        id: 'XXCMS0001',
        label: 'DIA',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: prArray[56],
      parentProduct: {
        id: 'XXPR0035',
        label: 'DIA - Kit',
      },
      parentMSServiceId: 'XXCMSS0071',
      location: {
        id: 'XXL0010',
        label: 'Sunset',
      },
      supplier: {
        id: 'XXS0002',
        label: 'Cogent',
      },
      supplierProductItem: findSPI(prArray[56].id, 'XXS0002'),
      itemAvailabilityState: getItemAvailabilityStateById(
        ItemAvailabilityIds.OPTIONAL_INCLUDED
      ),
      tsd: {
        id: 'XXT0001',
        hrid: 'T0001',
        descr: 'TBI',
        splitFraction: 0.85,
        spendAmount: 165000,
        numOpenOpportunities: 4,
        numSuppliers: 3,
        numCompanys: 180,
        channelManager: _getTCTDataById('XXTCT0002', tctListData),
        relationship: {
          id: 1,
          label: 'Direct',
        },
        lastActivityDate: '2022-03-01',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: false,
      },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.18,
      term: 36,
      kitQuantity: 'kit',
      quantity: 1,
      unitRcAmount: 79,
      mrcAmount: 0,
      unitNrcAmount: 150,
      nrcAmount: 0,
      oneTimeAmount: 250,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: '2022-10-01',
      closeByDate: '2022-04-01',
      status: getCompanyMSServiceStatusById(
        CompanyMSServicesStatusIds.READY_FOR_CONTRACT
      )!,
      doInclude: true,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCMSS0094',
      hrid: 'CMSS0094',
      descr: findSPI(prArray[62].id, 'XXS0002')?.descr || prArray[62].descr,
      masterService: {
        id: 'XXCMS0001',
        label: 'DIA',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: prArray[62],
      parentProduct: {
        id: 'XXPR0035',
        label: 'DIA - Kit',
      },
      parentMSServiceId: 'XXCMSS0071',
      location: {
        id: 'XXL0010',
        label: 'Sunset',
      },
      supplier: {
        id: 'XXS0002',
        label: 'Cogent',
      },
      supplierProductItem: findSPI(prArray[62].id, 'XXS0002'),
      itemAvailabilityState: getItemAvailabilityStateById(
        ItemAvailabilityIds.OPTIONAL_INCLUDED
      ),
      tsd: {
        id: 'XXT0001',
        hrid: 'T0001',
        descr: 'TBI',
        splitFraction: 0.85,
        spendAmount: 165000,
        numOpenOpportunities: 4,
        numSuppliers: 3,
        numCompanys: 180,
        channelManager: _getTCTDataById('XXTCT0002', tctListData),
        relationship: {
          id: 1,
          label: 'Direct',
        },
        lastActivityDate: '2022-03-01',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: false,
      },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.18,
      term: 36,
      kitQuantity: 2,
      quantity: 1,
      unitRcAmount: 0,
      mrcAmount: 0,
      unitNrcAmount: 250,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: true,
      quoteDate: '2022-10-01',
      closeByDate: '2022-04-01',
      status: getCompanyMSServiceStatusById(
        CompanyMSServicesStatusIds.READY_FOR_CONTRACT
      )!,
      doInclude: true,
      version: '1',
      canBeDeleted: true,
    },
    //
    //
    {
      id: 'XXCMSS0096',
      hrid: 'CMSS0096',
      descr: findSPI(pr0006!.id, 'XXS0009')?.descr || pr0006!.descr,
      masterService: {
        id: 'XXCMS0003',
        label: 'POTS',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: pr0006!,
      parentProduct: undefined,
      parentMSServiceId: undefined,
      location: {
        id: 'XXL0001',
        label: 'Boston',
      },
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      supplierProductItem: findSPI(pr0006!.id, 'XXS0009'),
      contract: { id: 'XXCON0005', label: 'POTS' },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.1,
      term: 36,
      kitQuantity: '1',
      quantity: 0,
      unitRcAmount: 25,
      mrcAmount: 0,
      unitNrcAmount: 0,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: '2024-08-01',
      closeByDate: '2024-09-30',
      status: getCompanyMSServiceStatusById(CompanyMSServicesStatusIds.ACTIVE)!,
      doInclude: true,
      fieldSetData:
        '{ "phoneNumber": "(555) 444-1111", "circuitId": "9238222" }',
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCMSS0097',
      hrid: 'CMSS0097',
      descr: findSPI(pr0006!.id, 'XXS0009')?.descr || pr0006!.descr,
      masterService: {
        id: 'XXCMS0003',
        label: 'POTS',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: pr0006!,
      parentProduct: undefined,
      parentMSServiceId: undefined,
      location: {
        id: 'XXL0001',
        label: 'Boston',
      },
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      supplierProductItem: findSPI(pr0006!.id, 'XXS0009'),
      contract: { id: 'XXCON0005', label: 'POTS' },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.1,
      term: 36,
      kitQuantity: '1',
      quantity: 0,
      unitRcAmount: 25,
      mrcAmount: 0,
      unitNrcAmount: 0,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: '2024-08-01',
      closeByDate: '2024-09-30',
      status: getCompanyMSServiceStatusById(CompanyMSServicesStatusIds.ACTIVE)!,
      doInclude: true,
      fieldSetData:
        '{ "phoneNumber": "(555) 444-2222", "circuitId": "9238223" }',
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCMSS0098',
      hrid: 'CMSS0098',
      descr: findSPI(pr0006!.id, 'XXS0009')?.descr || pr0006!.descr,
      masterService: {
        id: 'XXCMS0003',
        label: 'POTS',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: pr0006!,
      parentProduct: undefined,
      parentMSServiceId: undefined,
      location: {
        id: 'XXL0001',
        label: 'Boston',
      },
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      supplierProductItem: findSPI(pr0006!.id, 'XXS0009'),
      contract: { id: 'XXCON0005', label: 'POTS' },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.1,
      term: 36,
      kitQuantity: '1',
      quantity: 0,
      unitRcAmount: 25,
      mrcAmount: 0,
      unitNrcAmount: 0,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: '2024-08-01',
      closeByDate: '2024-09-30',
      status: getCompanyMSServiceStatusById(CompanyMSServicesStatusIds.ACTIVE)!,
      doInclude: true,
      fieldSetData:
        '{ "phoneNumber": "(555) 444-3333", "circuitId": "9238224" }',
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCMSS0099',
      hrid: 'CMSS0099',
      descr: findSPI(pr0006!.id, 'XXS0009')?.descr || pr0006!.descr,
      masterService: {
        id: 'XXCMS0003',
        label: 'POTS',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: pr0006!,
      parentProduct: undefined,
      parentMSServiceId: undefined,
      location: {
        id: 'XXL0001',
        label: 'Boston',
      },
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      supplierProductItem: findSPI(pr0006!.id, 'XXS0009'),
      contract: { id: 'XXCON0005', label: 'POTS' },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.1,
      term: 36,
      kitQuantity: '1',
      quantity: 0,
      unitRcAmount: 25,
      mrcAmount: 0,
      unitNrcAmount: 0,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: '2024-08-01',
      closeByDate: '2024-09-30',
      status: getCompanyMSServiceStatusById(CompanyMSServicesStatusIds.ACTIVE)!,
      doInclude: true,
      fieldSetData:
        '{ "phoneNumber": "(555) 444-4444", "circuitId": "9238225" }',
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCMSS0100',
      hrid: 'CMSS0100',
      descr: findSPI(pr0006!.id, 'XXS0009')?.descr || pr0006!.descr,
      masterService: {
        id: 'XXCMS0003',
        label: 'POTS',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: pr0006!,
      parentProduct: undefined,
      parentMSServiceId: undefined,
      location: {
        id: 'XXL0001',
        label: 'Boston',
      },
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      supplierProductItem: findSPI(pr0006!.id, 'XXS0009'),
      contract: { id: 'XXCON0005', label: 'POTS' },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.1,
      term: 36,
      kitQuantity: '1',
      quantity: 0,
      unitRcAmount: 25,
      mrcAmount: 0,
      unitNrcAmount: 0,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: '2024-08-01',
      closeByDate: '2024-09-30',
      status: getCompanyMSServiceStatusById(CompanyMSServicesStatusIds.ACTIVE)!,
      doInclude: true,
      fieldSetData:
        '{ "phoneNumber": "(555) 444-5555", "circuitId": "9238226" }',
      version: '1',
      canBeDeleted: true,
    },
    //
    {
      id: 'XXCMSS0101',
      hrid: 'CMSS0101',
      descr: findSPI(pr0006!.id, 'XXS0009')?.descr || pr0006!.descr,
      masterService: {
        id: 'XXCMS0003',
        label: 'POTS',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: pr0006!,
      parentProduct: undefined,
      parentMSServiceId: undefined,
      location: {
        id: 'XXL0009',
        label: 'Waltham',
      },
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      supplierProductItem: findSPI(pr0006!.id, 'XXS0009'),
      contract: { id: 'XXCON0005', label: 'POTS' },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.1,
      term: 36,
      kitQuantity: '1',
      quantity: 0,
      unitRcAmount: 25,
      mrcAmount: 0,
      unitNrcAmount: 0,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: '2024-08-01',
      closeByDate: '2024-09-30',
      status: getCompanyMSServiceStatusById(CompanyMSServicesStatusIds.ACTIVE)!,
      doInclude: true,
      fieldSetData:
        '{ "phoneNumber": "(555) 777-1111", "circuitId": "9289000" }',
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCMSS0102',
      hrid: 'CMSS0102',
      descr: findSPI(pr0006!.id, 'XXS0009')?.descr || pr0006!.descr,
      masterService: {
        id: 'XXCMS0003',
        label: 'POTS',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: pr0006!,
      parentProduct: undefined,
      parentMSServiceId: undefined,
      location: {
        id: 'XXL0009',
        label: 'Waltham',
      },
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      supplierProductItem: findSPI(pr0006!.id, 'XXS0009'),
      contract: { id: 'XXCON0005', label: 'POTS' },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.1,
      term: 36,
      kitQuantity: '1',
      quantity: 0,
      unitRcAmount: 25,
      mrcAmount: 0,
      unitNrcAmount: 0,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: '2024-08-01',
      closeByDate: '2024-09-30',
      status: getCompanyMSServiceStatusById(CompanyMSServicesStatusIds.ACTIVE)!,
      doInclude: true,
      fieldSetData:
        '{ "phoneNumber": "(555) 777-2222", "circuitId": "9289001" }',
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCMSS0103',
      hrid: 'CMSS0103',
      descr: findSPI(pr0006!.id, 'XXS0009')?.descr || pr0006!.descr,
      masterService: {
        id: 'XXCMS0003',
        label: 'POTS',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: pr0006!,
      parentProduct: undefined,
      parentMSServiceId: undefined,
      location: {
        id: 'XXL0009',
        label: 'Waltham',
      },
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      supplierProductItem: findSPI(pr0006!.id, 'XXS0009'),
      contract: { id: 'XXCON0005', label: 'POTS' },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.1,
      term: 36,
      kitQuantity: '1',
      quantity: 0,
      unitRcAmount: 25,
      mrcAmount: 0,
      unitNrcAmount: 0,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: '2024-08-01',
      closeByDate: '2024-09-30',
      status: getCompanyMSServiceStatusById(CompanyMSServicesStatusIds.ACTIVE)!,
      doInclude: true,
      fieldSetData:
        '{ "phoneNumber": "(555) 777-3333", "circuitId": "9289003" }',
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCMSS0104',
      hrid: 'CMSS0104',
      descr: findSPI(pr0006!.id, 'XXS0009')?.descr || pr0006!.descr,
      masterService: {
        id: 'XXCMS0003',
        label: 'POTS',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: pr0006!,
      parentProduct: undefined,
      parentMSServiceId: undefined,
      location: {
        id: 'XXL0009',
        label: 'Waltham',
      },
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      supplierProductItem: findSPI(pr0006!.id, 'XXS0009'),
      contract: { id: 'XXCON0005', label: 'POTS' },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.1,
      term: 36,
      kitQuantity: '1',
      quantity: 0,
      unitRcAmount: 25,
      mrcAmount: 0,
      unitNrcAmount: 0,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: '2024-08-01',
      closeByDate: '2024-09-30',
      status: getCompanyMSServiceStatusById(CompanyMSServicesStatusIds.ACTIVE)!,
      doInclude: true,
      fieldSetData:
        '{ "phoneNumber": "(555) 777-4444", "circuitId": "9289004" }',
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCMSS0105',
      hrid: 'CMSS0105',
      descr: findSPI(pr0006!.id, 'XXS0009')?.descr || pr0006!.descr,
      masterService: {
        id: 'XXCMS0003',
        label: 'POTS',
        parent: { id: 'XXC0001', label: 'Metropolis Bank' },
      },
      origin: {
        id: 1,
        label: 'New',
      },
      product: pr0006!,
      parentProduct: undefined,
      parentMSServiceId: undefined,
      location: {
        id: 'XXL0009',
        label: 'Waltham',
      },
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      supplierProductItem: findSPI(pr0006!.id, 'XXS0009'),
      contract: { id: 'XXCON0005', label: 'POTS' },
      recurs: getRecursPaidIntervalByLabel('Monthly'),
      paid: getRecursPaidIntervalByLabel('Monthly'),
      recursPaidDescr: 'M / M',
      splitFraction: 0.1,
      term: 36,
      kitQuantity: '1',
      quantity: 0,
      unitRcAmount: 25,
      mrcAmount: 0,
      unitNrcAmount: 0,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: '2024-08-01',
      closeByDate: '2024-09-30',
      status: getCompanyMSServiceStatusById(CompanyMSServicesStatusIds.ACTIVE)!,
      doInclude: true,
      fieldSetData:
        '{ "phoneNumber": "(555) 777-5555", "circuitId": "9289005" }',
      version: '1',
      canBeDeleted: true,
    },
  ]
  return [...startingRows, ..._installedInternetKitRows(startingRows)]
}
